import { gsap } from "gsap";
import Chart from 'chart.js/auto';
import jQuery, { data } from "jquery";
import { PageHandler } from './PageHandler';
import { ScrollTrigger } from "gsap/ScrollTrigger.js";
import { Utils } from "../misc/Utils";
import _Draggable from "gsap/Draggable";
// import * as monaco from 'monaco-editor/esm/vs/editor/editor.api';

import "daterangepicker";
import "daterangepicker/daterangepicker.css";

import { ISOAlpha3Countries } from "../misc/CountryCodes";

export class DesignPageHandler extends PageHandler {
	constructor(id) {
		super(id);

		/*************************************
		 * Design-specific
		 *************************************/
		Chart.defaults.font.family = "'Manrope', 'Arial', 'Helvetica', sans-serif"
		Chart.defaults.font.size = 14;

		this.energy_reference = {
			cooling: 0.0,
			heating: 0.0,
			lighting: 0.0,
			pv: 0.0,
		};

		this.modelViewer = document.querySelector('.results__model model-viewer')
		this.$modelViewer = jQuery(this.modelViewer);
		window.modelViewer = this.modelViewer;

		this.bipvModels = {
			"Reference": {
				tableID: 62,
				imageURL: "/wp-content/uploads/2024/06/Reference.png",
				modelURL: DesignPageHandler.get3DModelURL("reference.glb", true)
			},
			"Opaque facade": {
				tableID: 64,
				imageURL: "/wp-content/uploads/2024/06/Opaque_Facade.png", 
				modelURL: DesignPageHandler.get3DModelURL("pv_facade.glb", true)
			},
			"STPV a-Si": {
				tableID: 65,
				imageURL: "/wp-content/uploads/2024/06/STPV1.png" ,
				modelURL: DesignPageHandler.get3DModelURL("stpv1.glb", true)
			},
			"STPV c-Si": {
				tableID: 66,
				imageURL: "/wp-content/uploads/2024/06/STPV2.png",
				modelURL: DesignPageHandler.get3DModelURL("stpv2.glb", true)
			},
			"PV louvers": {
				tableID: 67,
				imageURL: "/wp-content/uploads/2024/06/PVlouvers.png",
				modelURL: DesignPageHandler.get3DModelURL("pv_louvers.glb", true)
			},
			"PV overhang": {
				tableID: 68,
				imageURL: "/wp-content/uploads/2024/06/PVoverhang.png", 
				modelURL: DesignPageHandler.get3DModelURL("pv_overhang.glb", true)
			}
		};
	}

	static get3DModelURL(filename, compressed = false) {
		let baseURL = "/wp-content/themes/bipv-city/assets/models/"
		if (compressed)
			baseURL += "compressed/"

		return baseURL + filename
	}

	
	static inMonthWindow(dateA, dateB) {
		const MS_PER_DAY = 24 * 60 * 60 * 1000; // Milliseconds in one day
		const diffInMs = Math.abs(dateB - dateA);
		const diffInDays = diffInMs / MS_PER_DAY;
		return diffInDays <= 31;
	}

	static getAllValuesForKeyWithinRange(data, keys, minDate, maxDate) {
		function calculateDailyAverages(data, valueKeys) {
			const groupedByDay = data.reduce((acc, record) => {
				const dayKey = `${record.year}-${record.month}-${record.day}`;
				if (!acc[dayKey]) {
					acc[dayKey] = { year: record.year, month: record.month, day: record.day, sums: {}, counts: {} };
					valueKeys.forEach(key => {
						acc[dayKey].sums[key] = 0;
						acc[dayKey].counts[key] = 0;
					});
				}
				valueKeys.forEach(key => {
					acc[dayKey].sums[key] += parseFloat(record[key]); 
					acc[dayKey].counts[key] += 1;
				});
				return acc;
			}, {});
			
			return Object.values(groupedByDay).map(({ year, month, day, sums, counts }) => {
				const averages = valueKeys.reduce((result, key) => {
					result[key] = sums[key] / counts[key];
					return result;
				}, {});
				return { year, month, day, ...averages };
			});
		}

		let values = data
			.filter(item => {
				const itemDate = new Date(
					parseInt(item.year),
					parseInt(item.month) - 1,
					parseInt(item.day),
					parseInt(item.hour),
					parseInt(item.minute)
				);

				return itemDate >= minDate && itemDate <= maxDate;
			})
			// .map(item => item[key]);

		if (!DesignPageHandler.inMonthWindow(minDate, maxDate))
			return calculateDailyAverages(values, keys);

		return values;
	}

	ajax() {

	}
	
	/* Creates a chart with the climate data */
	climateRenderEPWChart(minDate, maxDate) {
		var _this = this;
		const ctx = document.getElementById('epw-chart');
		let humTempValues = DesignPageHandler.getAllValuesForKeyWithinRange(
			window.epw_data.data, ["relativeHumidity", "dryBulbTemperature", "dewPointTemperature"], minDate, maxDate
		);

        const timestamps = humTempValues.map(item => 
            DesignPageHandler.inMonthWindow(minDate, maxDate) ? 
				`${item.year}-${item.month}-${item.day} ${item.hour}:${item.minute}`
				: `${item.year}-${item.month}-${item.day}`
        );
        const relativeHumidity = humTempValues.map(item => item['relativeHumidity'])
        const dewPointTemp = humTempValues.map(item => item['dewPointTemperature'])
        const dryBulbTemp = humTempValues.map(item => item['dryBulbTemperature'])

		const config = {
            type: 'line',
            data: {
                labels: timestamps, // X-axis labels
                datasets: [
					{
						label: 'Relative Humidity (%)',
						data: relativeHumidity,
						borderColor: '#64ba77',
						backgroundColor: "rgb(120, 224, 143)",
						fill: false,
						cubicInterpolationMode: 'monotone',
						tension: 0.4
					},
					{
						label: 'Dew Point Temperature (°C)',
						data: dewPointTemp,
						backgroundColor: 'rgb(255, 64, 104)',
						borderColor: '#bd3061',
						fill: false,
						cubicInterpolationMode: 'monotone',
						tension: 0.4
					},
					{
						label: 'Dry Bulb Temperature (°C)',
						data: dryBulbTemp,
						borderColor: '#fbad6c',
						backgroundColor: 'rgb(255, 204, 109)',
						fill: false,
						cubicInterpolationMode: 'monotone',
						tension: 0.4
					}
                ]
            },
            options: {
                responsive: true,
				maintainAspectRatio: false,
				layout: {
					padding: {
						top: 30,
						right: 30,
						bottom: 30,
						left: 30
					}
				},
				animation: {
					onComplete: function() {
						gsap.to('.section__design-in--climate .results__chart-container .loading-screen', {autoAlpha: 0, duration: 0.3, overwrite: true})
						jQuery('.section__design-in--climate .results__chart-container .loading-screen').addClass('hidden')			
					}
				},
                plugins: {
                    legend: {
                        position: 'top',
                    },
                    title: {
                        display: true,
                        text: 'Humidity & Temperatures'
                    }
                },
				point: {
					backgroundColor: 'white'
				},
                scales: {
                    x: {
                        title: {
                            display: true,
                            text: 'Dates',
                        },
						gridLines: {
							color: 'rgba(200, 200, 200, 0.08)',
							lineWidth: 1
						},
                        ticks: {
                            maxTicksLimit: 5 // Limit the number of ticks for readability
                        }
                    },
                    y: {
                        title: {
                            display: true,
                            text: 'Values',
                        },
						gridLines: {
							color: 'rgba(200, 200, 200, 0.08)',
							lineWidth: 1
						}
                    }
                }
            }
		}
		

		if (this.epwChart)
			this.epwChart.destroy();
		this.epwChart = new Chart(ctx, config);

		window.epwChart = this.epwChart;
	}

	/* Creates a chart with the BIPV design results */
	designRenderBIPVChart(label, energy) {
		const ctx = document.getElementById('results-chart');
		const labels = ["Reference"];
		const datasets = {
			cooling: [this.energy_reference.cooling],
			heating: [this.energy_reference.heating],
			lighting: [this.energy_reference.lighting],
			pv: [this.energy_reference.pv]
		}
		
		window.hasNegativeValues = false;
		if (label !== "Reference")
		{
			labels.push(label)
			datasets.cooling.push(energy.cooling)
			datasets.heating.push(energy.heating)
			datasets.lighting.push(energy.lighting)
			datasets.pv.push(energy.pv)
			window.hasNegativeValues = (datasets.pv[1] < 0.0);
		}

		const data = {
			labels: labels,
			backdropPadding: {
				bottom: 50
			},
			datasets: [
				{
					label: 'Cooling',
					data: datasets.cooling,
					backgroundColor: Utils.CHART_COLORS.blue,
					
				},
				{
					label: 'Heating',
					data: datasets.heating,
					backgroundColor: Utils.CHART_COLORS.red,
				},
				{
					label: 'Lighting',
					data: datasets.lighting,
					backgroundColor: Utils.CHART_COLORS.yellow,
				},
				{
					label: 'PV',
					data: datasets.pv,
					backgroundColor: Utils.CHART_COLORS.green,
				},
			]
		};
	
		const config = {
			type: 'bar',
			data: data,
			plugins: [{
				afterDraw: function(chart) {
					const yAxis = chart.scales['y'];
        			const ctx = chart.ctx;
					ctx.save();
					window.yHeight = yAxis.height;
					window.yZero = yAxis.getPixelForValue(0);
					window.yNegValuePixel = 0;
					window.firstTick = yAxis.getTicks()[0].value
					window.lastTick = yAxis.getTicks()[yAxis.getTicks().length - 1].value
					window.yAxis = yAxis;
					window.diff = 0;

					if (window.hasNegativeValues)
					{
						window.diff = yAxis.getPixelForValue(firstTick) - yAxis.getPixelForValue(0);
						window.yNegValuePixel = yAxis.getPixelForValue(window.maxNegativeValue);
						jQuery('.y2label--two').css('top', window.yZero);
						jQuery('.y2label--two').css('top', yAxis.getPixelForValue(0) + (diff)/ 2);
					}

					jQuery('.y2label--one').css('top', yAxis.getPixelForValue(lastTick) + (yAxis.height - diff)/ 2);
					ctx.restore();
				}
			}],
			options: {
				animation: {
					onComplete: function() {
						gsap.to('.section__design-in--design .results__chart-container .loading-screen', {autoAlpha: 0, duration: 0.3, overwrite: true})
						jQuery('.section__design-in--design .results__chart-container .loading-screen').addClass('hidden')
						gsap.set('.y2label--one', {width: yAxis.height - window.diff, right: -(yAxis.height - window.diff) / 2 + 20})
						gsap.to('.section__design-in--design .results__chart-container .y2label--one', {autoAlpha: 1, duration: 0.3})

						if (window.hasNegativeValues) {
							gsap.set('.y2label--two', {width: window.diff, right: -(window.diff) / 2 + 20})
							gsap.to('.section__design-in--design .results__chart-container .y2label--two', {autoAlpha: 1, duration: 0.3})
						}
					}
				},
				layout: {
					padding: {
						bottom: 55
					}
				},
				plugins: {
					datalabels: {
						align: 'top'
					},
					title: {
						display: true,
						text: 'Energy',
						font: {
							size: 16
						}
					},
					
				},
				responsive: true,
				maintainAspectRatio: false,
				barThickness: 40,
				scales: {
					x: {
						stacked: true,
					},
					y: {
						type: 'linear',
						position: 'left',
						stacked: true,
						title: {
							display: true,
							text: "Total Energy Demand (kWh/m²)"
						},
					},
					y2: {
                        type: 'linear',
                        position: 'right',
                        grid: {
                            drawOnChartArea: false 
                        },
                        ticks: {
                            display: false
                        },
						title: {
                            display: true,
                        },
                    }
				
				}
			}
		};

		if (this.chart)
			this.chart.destroy();
		this.chart = new Chart(ctx, config);

		window.chart = this.chart;
	}

	changeTab(tab) {
		var newView = jQuery(`.section__design-in[data-view="${tab}"]`);
		if (!newView.length) return;

		jQuery('.section__design-in.active').removeClass('active')
		newView.addClass('active');
	}

	/* Change the format of EPW data sources */
	climateChangeEPWSourcesFormat(fmt) {
		var newFormat = jQuery(`.source-code__selections-item button[data-format="${fmt}"]`);
		if (!newFormat.length) return;
		if (fmt == "json")
		{
			// this.loadMonacoEditor(JSON.stringify(window.epw_data, null, 2));
			this.climateLoadPlainEditor(JSON.stringify(window.epw_data, null, 2))
		}
		else if (fmt == "raw")
		{
			// this.loadMonacoEditor(window.epw_data_raw);
			this.climateLoadPlainEditor(window.epw_data_raw)
		}

	}

	/* Downloads the EPW data source in a specific format */
	climateDownloadEPWSources(fmt) {
		const text = (fmt == "json" ? JSON.stringify(window.epw_data, null, 2) : window.epw_data_raw)
		const blob = new Blob([text], { type: "text/plain" });
		const url = URL.createObjectURL(blob);
		const a = document.createElement("a");
		a.href = url;
		a.download = window.epw_filename + (fmt == "json" ? ".json" : ""); // Specify the file name
		a.click();
		URL.revokeObjectURL(url);
	}

	/* Loads a 3D model into the model viewer */
	designLoadModel(name) {
		this.$modelViewer.attr('poster', this.bipvModels[name].imageURL);
		this.$modelViewer.attr('src', this.bipvModels[name].modelURL);
	}

	/* Loads a table with the results of BIPV design
	 * and then creates a graph out of them
	 */
	designLoadTable(name, id) {
		jQuery.ajax({
            url: `/wp-json/ninjatable/v1/table/${id}`,
            type: 'GET',
            dataType: 'json',
            data: {
                table_id: id,
                page: 1,
				per_page: 20,
				default_storing: "old_first",
				location: window.locationName
            },
            success: (response) => {
				let energy = {
					cooling: response['energy_cooling'],
					heating: response['energy_heating'],
					lighting: response['energy_lighting'],
					pv: response['energy_pv']
				};
                document.querySelectorAll('td.column').forEach((elem, index) => {
					var colID = jQuery(elem).data('column');
					var data = response[colID];
					elem.textContent = data;
				})
				
				if (name == "Reference")
					this.energy_reference = energy;

				this.designRenderBIPVChart(name, energy);
				gsap.to('.results__table .loading-screen', {autoAlpha: 0, duration: 0.3, overwrite: true})
				jQuery('.results__table .loading-screen').addClass('hidden')

            },
            error: function(xhr, status, error) {
                console.error('AJAX Error:', status, error);
            }
        });

    }

	/* Loads a plain text editor with the EPW data sources */
	climateLoadPlainEditor(data) {
		var _this = this;

		window.chunkedData = data.split('\n')
		const CHUNK_SIZE = 1000; // Number of lines to load per chunk
		let loadedLines = 0; // Track the number of lines currently loaded

		/* Create a container for the editor in your HTML */
		const container = document.getElementById('my-code');
		const textarea = container.querySelector('textarea');

		/* Clear any existing content */
		textarea.value = '';
		textarea.scrollTop = 0;
		/* Load a chunk of lines into the textarea */
		function loadChunk() {
			if (loadedLines < chunkedData.length) {
				const nextChunk = chunkedData.slice(loadedLines, loadedLines + CHUNK_SIZE).join('\n');
				textarea.value += (textarea.value ? '\n' : '') + nextChunk;
				loadedLines += CHUNK_SIZE;
			}
		}
	
		/* Load the initial chunk */
		loadChunk();
	
		/* Add a scroll event listener for the textarea */
		textarea.addEventListener('scroll', () => {
			const scrollHeight = textarea.scrollHeight;
			const scrollTop = textarea.scrollTop;
			const clientHeight = textarea.clientHeight;
	
			/* Check if the user has scrolled near the bottom */
			if (scrollTop + clientHeight >= scrollHeight - 10) {
				loadChunk();
			}
		});

	}


	/* Appends the left side of the Climate tab (location, map, data periods etc.) */
	climateAppendDataPaneLeft() {
		var _this = this;
		let location = window.epw_data['location'];
		let dataPeriods = window.epw_data['dataPeriods'];
		let $location_section = jQuery('.section__design-in--climate .section__settings-location');
		let $location_table = jQuery('.section__design-in--climate .section__settings-location .vertical__table .vertical');
		let $data_periods_section = jQuery('.section__design-in--climate .section__settings-data-periods');
		
		/* Location */
		let locationTable = ``;
		for (var key in location)
		{
			var value = location[key];
			var capitalizedKey = String(key).charAt(0).toUpperCase() + String(key).slice(1);
			if (capitalizedKey == "Timezone")
				value = "UTC+" + value
			if (capitalizedKey == "Country" && ISOAlpha3Countries.COUNTRIES[value])
				value = `${value} (${ISOAlpha3Countries.COUNTRIES[value]})`;
			locationTable += `<tr><th class="subheading">${capitalizedKey}</th> <td class="column">${value}</td></tr>`;
		}

		/* Map */
		let iframe = jQuery(`<iframe src="//maps.google.com/maps?q=${location['latitude']},${location['longitude']}&z=15&output=embed"></iframe>`)
		let showMapButton = jQuery(`<button type="button" class="button secondary show-map-btn">Show location map</button>`)

		$location_table.html(locationTable);
		$location_section.append(showMapButton);
		$location_section.append(iframe);

		showMapButton.on('click', () => {
			iframe.toggleClass('active')
			if (iframe.hasClass('active'))
				showMapButton.text('Hide location map')
			else
				showMapButton.text('Show location map')
		});

		/* Data Periods */
		let dataPeriodsTable = ``;
		dataPeriods.forEach((value, index) => {
			dataPeriodsTable = ``;
			for (var key in value)
			{
				var val = value[key];
				dataPeriodsTable += `<tr><th class="subheading">${key}</th> <td class="column">${val}</td></tr>`;
			}
			dataPeriodsTable = `<div class="vertical__table"><table class="vertical">${dataPeriodsTable}</table></div>`;
			$data_periods_section.append(dataPeriodsTable);
		})
	}

	showScrollHint(tableSelector, containerSelector) {
		if (jQuery(tableSelector).height() <= jQuery(containerSelector).height()) 
			return;

		const hint = `${containerSelector} .please-scroll`;
		gsap.to(hint, { autoAlpha: 0.9, duration: 0.3 });
		setTimeout(() => {
			gsap.to(hint, { autoAlpha: 0, duration: 0.3 });
		}, 2000);
	}

	climateCreateDataTable(tableID, epwData, parent)	{
		var _this = this;
		let header = ['Datetime', 'GHI', 'DHI', 'DNI', 'T<sub>out</sub>', 'Wind speed', 'Wind direction'];

		const thead = document.createElement("thead"), 
		      theadRow = document.createElement("tr"),
		      tbody = document.createElement("tbody");
		const table = document.getElementById(tableID);

		epwData.data.sort((a, b) =>
			Number(a.month) - Number(b.month)
		);
		
		if (epwData['data'].length > 0)
		{
			header.forEach(element => {
				const th = document.createElement("th");
				jQuery(th).html(element)
				theadRow.appendChild(th);
			});
			thead.appendChild(theadRow);
		}

		table.appendChild(thead);
		table.appendChild(tbody);

		/* Chunk loading */
		let loadedLines = 0;
		const CHUNK_SIZE = 19; 
		parent.scrollTop = 0;
		jQuery(parent).off('scroll')

		const merged = epwData['data'].map((row, i) => {
			const values = Array.isArray(row) ? row : [row];
			return [
				`${row.day}/${row.month}/${row.year}, ${row.hour}:${row.minute.padStart(2, '0')}`,
				row.globalHorizontalRadiation,
				row.diffuseHorizontalRadiation,
				row.directNormalRadiation,
				row.dryBulbTemperature,
				row.windSpeed,
				row.windDirection
			];
		});

		function loadChunk() {
			if (loadedLines < merged.length) {
				
				const nextChunk = merged.slice(loadedLines, loadedLines + CHUNK_SIZE);

				nextChunk.forEach(row => {
					const tr = document.createElement("tr");
					row.forEach(cell => {
						const td = document.createElement("td");
						td.textContent = cell;
						tr.appendChild(td);
					});
					tbody.appendChild(tr);
				});
				loadedLines += CHUNK_SIZE;
			}
		}
	
		loadChunk();
		
		jQuery(parent).on('scroll', () => {
			const scrollHeight = parent.scrollHeight;
			const scrollTop = parent.scrollTop;
			const clientHeight = parent.clientHeight;

			if (scrollTop + clientHeight >= scrollHeight - 100) {
				loadChunk();
			}
		});

		this.showScrollHint('#epw-table', '.section__design-right .return-results-container');
	}

	/* Appends a collection of year buttons to the right side of
	 * the Climate tab, allowing the user to select a year
	 */
	climateAppendYearBtns(years) {
		years.forEach((value, index) => {
			jQuery('.chart-date-range--years .chart-date-range__in').append(
				`<button type="button" class="button secondary update-chart-year-btn">${value}</button>`
			)
		})
	}

	initClimatePage() {
		window.epw_data.data.sort((a, b) => a.year - b.year);
		const data = window.epw_data.data;

		var minDate = new Date(
			parseInt(data[0].year), parseInt(data[0].month - 1), parseInt(data[0].day),
			parseInt(data[0].hour), parseInt(data[0].minute)
		);
		var maxDate = new Date(
			parseInt(data[data.length-1].year), parseInt(data[data.length-1].month - 1), parseInt(data[data.length-1].day),
			parseInt(data[data.length-1].hour), parseInt(data[data.length-1].minute)
		);

		const validDateSet = new Set(
			window.epw_data.data.map(
				(date) => `${date.year}-${String(date.month).padStart(2, "0")}-${String(date.day).padStart(2, "0")}`
			)
		);

		// this.loadMonacoEditor(JSON.stringify(window.epw_data, null, 2));
		this.climateLoadPlainEditor(JSON.stringify(window.epw_data, null, 2))
		this.climateAppendDataPaneLeft();
			
		this.climateCreateDataTable('epw-table', window.epw_data, 
			document.querySelector('.section__design-right .return-results-container .return-results'));

		this.climateRenderEPWChart(minDate, maxDate);
		this.climateAppendYearBtns(new Set(window.epw_data.data.map(date => date.year)))
		
		jQuery('#graph-daterange').daterangepicker({
			showDropdowns: true,
			autoApply: true,
			locale: "DD/MM/YYYY",
			startDate: `${data[0].month}/${data[0].day}/${data[0].year}`,
			endDate: `${data[data.length-1].month}/${data[data.length-1].day}/${data[data.length-1].year}`,
			minDate: `${data[0].month}/${data[0].day}/${data[0].year}`,
			maxDate: `${data[data.length-1].month}/${data[data.length-1].day}/${data[data.length-1].year}`,
			isInvalidDate: (date) => {
				const formattedDate = date.format("YYYY-MM-DD");
				// If the date is not in the validDateSet, mark it as invalid
				return !validDateSet.has(formattedDate);
			}
		}, function(start, end, label) {
			window.chartDateStart = start;
			window.chartDateEnd = end;
		});
	}

	initDesignPage() {
		window.selectedModel = jQuery('main.design .section__settings-model .custom-select-wrapper .select-selected').data('value')

		window.global.initSelect('main.design .section__settings-model .custom-select-wrapper', (option) => {
			var value = jQuery(option).data('value');

			if (window.selectedModel == value)
				return;

			window.selectedModel = value;

			gsap.to('.shortcuts__pane-ctrls', {autoAlpha: 0, duration: 0.1})
			jQuery('.loading-screen').removeClass('hidden')
			gsap.to('.results__chart-container .y2label', {autoAlpha: 0, duration: 0.3})

			gsap.to('.loading-screen', {autoAlpha: 1, duration: 0.3, overwrite: true, onComplete: () => {
				this.designLoadModel(value);
				if ((typeof window.do_bipv_design === 'undefined') || (window.do_bipv_design == true))
				{
					this.designLoadTable(value, this.bipvModels[value].tableID)
				}
			}})
		});
	}

	init() {
		this.events();

		if (window.epw_data_available)
			this.initClimatePage();
		else
			this.initDesignPage();
	}

	destroy() {

	}

	/* Events for the Design tab */
	designEvents() {
		/* Design view -- 3D Model */
		this.modelViewer.addEventListener('load', () => {
			this.$modelViewer.attr('camera-orbit', "-30deg auto auto");
			gsap.to('.results__model .loading-screen', {autoAlpha: 0, duration: 0.3, overwrite: true})
			jQuery('.results__model .loading-screen').addClass('hidden')
			this.modelViewer.zoom(20)
		});

		/* Design view -- 3D Model: Shortcuts */
		jQuery('.shortcuts__text').on('click', function() {
			gsap.to('.shortcuts__pane-mask, .shortcuts__pane-ctrls', {autoAlpha: 1, duration: 0.3})
		});

		jQuery('.shortcuts__pane-mask').on('click', function() {
			gsap.to('.shortcuts__pane-mask, .shortcuts__pane-ctrls', {autoAlpha: 0, duration: 0.3})
		});

		jQuery('.shortcuts__pane .close-btn').on('click', function() {
			gsap.to('.shortcuts__pane-mask, .shortcuts__pane-ctrls', {autoAlpha: 0, duration: 0.3})
		});
	}

	/* Events for the Climate tab */
	climateEvents() {
		var _this = this;
		
		/* Climate view -- Change sources format */
		jQuery('body').on('click', '.source-code__selections-item button.secondary', function() {
			var fmt = jQuery(this).data('format')
			jQuery('.source-code__selections-item button.primary').removeClass('primary').addClass('secondary')
			jQuery(this).removeClass('secondary').addClass('primary')
			_this.climateChangeEPWSourcesFormat(fmt);
		})

		/* Climate view -- Download sources */
		jQuery('body').on('click', '.source-code__download', function() {
			var fmt = jQuery('.source-code__selections-item button.primary').data('format')
			_this.climateDownloadEPWSources(fmt);
		})

		jQuery('body').on('click', '.update-chart-btn', function() {
			_this.climateRenderEPWChart(window.chartDateStart.toDate(), window.chartDateEnd.toDate());
		})

		jQuery('body').on('click', '.update-chart-year-btn', function() {
			var year = jQuery(this).text();
			_this.climateRenderEPWChart(new Date(parseInt(year), 0), new Date(parseInt(year), 11, 31));
		})
	}

	events() {
		var _this = this;
		let mm = gsap.matchMedia();
		mm.add("(min-width: 1025px) and (min-height: 950px)", function() {
			Chart.defaults.font.size = 14;

			/* Design view -- Pin parameters on scrolling */
			ScrollTrigger.create({
				trigger: '.section__design-left',
				start: 'top top+=150px',
				end: 'top bottom',
				endTrigger: 'footer',
				pin: '.section__settings-parameters',
				pinSpacing: false,
				invalidateOnRefresh: true
			})
		})

		mm.add("(max-width: 768px)", function() {
			Chart.defaults.font.size = 12;
		});

		/* Tab selection */
		jQuery('body').on('click', '.section__design-tab:not(.active):not(.disabled)', function() {
			var view = jQuery(this).data('tab')
			jQuery('.section__design-tab.active').removeClass('active')
			jQuery(this).addClass('active')
			_this.changeTab(view);
		})
		
		this.designEvents();
		this.climateEvents();
	}

	transitions() {
		this.designLoadModel("Reference");
		if ((typeof window.do_bipv_design === 'undefined') || (window.do_bipv_design == true))
			this.designLoadTable("Reference", this.bipvModels["Reference"].tableID)
		else 
			gsap.set('.section__design-in[data-view=design] .results__table, .section__design-in[data-view=design] .results__chart-container', {display: "none"})
	}
}
